import React, { useMemo } from 'react';
import type { BranchIncomeRecord, ExpenseRecord, Student } from '../../types/db';
import { TrendingUp, TrendingDown, Wallet, Building2, Sparkles, PieChart } from 'lucide-react';

interface FinanceDashboardProps {
  incomes: BranchIncomeRecord[];
  expenses: ExpenseRecord[];
  students: Student[];
  branchId: string;
}

export function FinanceDashboard({
  incomes = [],
  expenses = [],
  students = [],
  branchId = 'ALL',
}: FinanceDashboardProps) {
  const isConsolidated = branchId === 'ALL';
  const isHQ = branchId === 'HEADQUARTERS';

  const uiLabels = useMemo(() => {
    if (isConsolidated) {
      return {
        title: 'KONSOLIDASI JARINGAN',
        subtitle: 'Akumulasi Kas Seluruh Unit & Pusat',
        color: 'text-indigo-600',
      };
    }
    if (isHQ) {
      return {
        title: 'KAS PUSAT (INTERNAL)',
        subtitle: 'Laporan Pendapatan Registrasi & HQ',
        color: 'text-amber-600',
      };
    }
    return {
      title: 'KAS UNIT CABANG',
      subtitle: 'Laporan Keuangan Operasional Cabang',
      color: 'text-emerald-600',
    };
  }, [isConsolidated, isHQ]);

  const financialStats = useMemo(() => {
    const filteredIncomes = incomes.filter((i) => {
      if (isConsolidated) return true;
      return i.branch_id === branchId;
    });

    const filteredExpenses = expenses.filter((e) => {
      if (isConsolidated) return true;
      return e.branch_id === branchId;
    });

    const totalIn = filteredIncomes.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const totalOut = filteredExpenses.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const profit = totalIn - totalOut;

    return { totalIn, totalOut, profit };
  }, [incomes, expenses, branchId, isConsolidated]);

  return (
    <div className="space-y-8 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className={`w-5 h-5 ${uiLabels.color}`} />
            <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase">
              {uiLabels.title}
            </h2>
          </div>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            {uiLabels.subtitle}
          </p>
        </div>

        <div className="bg-slate-50 px-4 py-2 rounded-xl border border-slate-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" />
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
            Scope: {branchId}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-bl-full opacity-50 -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
          <TrendingUp className="text-emerald-500 mb-6 w-10 h-10" />
          <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">
            Total Pemasukan
          </p>
          <h3 className="text-3xl font-black text-slate-900 tracking-tighter">
            Rp {financialStats.totalIn.toLocaleString()}
          </h3>
        </div>

        <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-bl-full opacity-50 -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
          <TrendingDown className="text-rose-500 mb-6 w-10 h-10" />
          <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1">
            Total Pengeluaran
          </p>
          <h3 className="text-3xl font-black text-slate-900 tracking-tighter">
            Rp {financialStats.totalOut.toLocaleString()}
          </h3>
        </div>

        <div
          className={`p-10 rounded-[3rem] text-white shadow-2xl transition-all group relative overflow-hidden ${
            financialStats.profit >= 0
              ? 'bg-indigo-600 shadow-indigo-100'
              : 'bg-rose-600 shadow-rose-100'
          }`}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
          <Wallet className="text-white/80 mb-6 w-10 h-10" />
          <p className="text-[10px] font-black uppercase text-white/70 tracking-[0.2em] mb-1">
            Saldo Bersih (Kas)
          </p>
          <h3 className="text-3xl font-black tracking-tighter">
            Rp {financialStats.profit.toLocaleString()}
          </h3>
        </div>
      </div>

      {isConsolidated && (
        <div className="bg-indigo-50 p-6 rounded-[2rem] border border-indigo-100 flex items-center gap-4">
          <PieChart className="text-indigo-600 w-8 h-8 shrink-0" />
          <p className="text-[11px] font-medium text-indigo-900 leading-relaxed">
            <span className="font-black uppercase">Informasi Konsolidasi:</span> Saldo ini adalah
            akumulasi dari dana pusat (Registrasi) dan dana unit cabang (SPP/Kursus). Silakan pilih
            filter spesifik untuk melihat rincian kas masing-masing unit.
          </p>
        </div>
      )}
    </div>
  );
}
